import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import {
  Component, ElementRef, EventEmitter, HostListener, Input, Output,
  computed, inject, signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { InputComponent } from '../../atoms/input/input.component';
import { SpinnerComponent } from '../../atoms/spinner/spinner.component';

/**
 * Generic type-ahead picker. Works for any item shape — the consumer
 * provides adapter functions (`trackByFn`, `labelFn`, optional
 * `sublabelFn`, optional `matchFn`) and Typeahead handles the filtering,
 * dropdown, single/multi selection, and outside-click semantics.
 *
 * Two modes:
 *   - **single** (default): pick one item. Query field is populated with
 *     the picked item's label; dropdown closes on selection. Emits
 *     `itemSelected` with the picked item.
 *   - **multiple** (`[multiple]="true"`): pick several items. Selected
 *     items render as chips inline with the input; already-picked items
 *     are excluded from suggestions; dropdown stays open after each pick.
 *     Emits `selectionChanged` (full list) on every change, plus
 *     `itemSelected` on each individual add.
 *
 * For custom suggestion rendering (avatars, badges, etc.) pass an
 * `[itemTemplate]` — falls back to a label / sublabel two-line layout
 * when omitted.
 *
 * Server-side search: subscribe to `queryChanged` and update `items`
 * externally. Client-side filtering is bypassed by passing your own
 * `matchFn` that returns `true` (treat all items as matches).
 */
@Component({
  selector: 'es-typeahead',
  imports: [FormsModule, NgTemplateOutlet, InputComponent, SpinnerComponent],
  template: `
    <div class="relative">
      @if (multiple) {
        <!-- Multi mode: chips + inline freeform input -->
        <div
          class="flex flex-wrap items-center gap-1.5 px-2 py-1.5 min-h-[40px] rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] focus-within:border-echo-blue focus-within:ring-1 focus-within:ring-echo-blue/30 transition-colors cursor-text"
          (click)="focusInput()"
        >
          @for (item of selected(); track trackByFn(item)) {
            <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-echo-blue/20 text-echo-blue">
              {{ labelFn(item) }}
              <button
                type="button"
                class="text-echo-blue/70 hover:text-echo-blue cursor-pointer leading-none"
                (click)="deselect(item); $event.stopPropagation()"
                [attr.aria-label]="'Remove ' + labelFn(item)"
              >×</button>
            </span>
          }
          <input
            type="text"
            class="flex-1 min-w-[140px] bg-transparent outline-none text-sm font-exo text-[var(--text)] placeholder:text-[var(--text-secondary)]"
            [placeholder]="selected().length === 0 ? placeholder : ''"
            [(ngModel)]="query"
            (ngModelChange)="onQueryChange()"
            (focus)="open.set(true)"
          />
        </div>
      } @else {
        <!-- Single mode: standard themed input -->
        <es-input
          [placeholder]="placeholder"
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange()"
          (focus)="open.set(true)"
        ></es-input>
      }

      @if (open()) {
        <div
          class="absolute left-0 right-0 mt-1 max-h-72 overflow-auto rounded-lg bg-[var(--bg-card)] border border-[var(--border)] shadow-lg z-50 animate-scale-in"
        >
          @if (loading) {
            <div class="p-3"><es-spinner label="Loading…"></es-spinner></div>
          } @else if (results().length === 0) {
            <p class="px-3 py-2 text-sm text-[var(--text-secondary)]">
              {{ effectiveEmptyText() }}
            </p>
          } @else {
            @for (item of results(); track trackByFn(item)) {
              <button
                type="button"
                class="w-full text-left hover:bg-[var(--bg-card-hover)] cursor-pointer transition-colors"
                (click)="select(item)"
              >
                @if (itemTemplate) {
                  <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item }"></ng-container>
                } @else {
                  <div class="px-3 py-2">
                    <p class="text-sm font-medium truncate">{{ labelFn(item) }}</p>
                    @if (sublabelFn && sublabelFn(item); as sub) {
                      <p class="text-xs text-[var(--text-secondary)] truncate">{{ sub }}</p>
                    }
                  </div>
                }
              </button>
            }
          }
        </div>
      }
    </div>
  `,
})
export class TypeaheadComponent<T = unknown> implements OnChanges {
  @Input() items: T[] = [];
  @Input() loading = false;
  @Input() multiple = false;
  @Input() placeholder = 'Search…';
  /** Single-mode pre-selection. */
  @Input() value: T | null = null;
  /** Multi-mode pre-selection. */
  @Input() values: T[] = [];
  /** Text shown when the dropdown is open but `items` is empty (no filtering applied). */
  @Input() noItemsText = 'No items available';
  /** Text shown when the query has no matches. */
  @Input() noMatchesText = 'No matches';
  /** Text shown in multi mode when every available item is already picked. */
  @Input() allPickedText = 'All items are picked';

  /** Stable identity per item — used for tracking + multi-mode exclusion + chip removal. */
  @Input({ required: true }) trackByFn!: (item: T) => string;
  /** Primary label rendered in chips, default suggestion line, and (single-mode) the input. */
  @Input({ required: true }) labelFn!: (item: T) => string;
  /** Optional secondary line under the primary label. */
  @Input() sublabelFn?: (item: T) => string | undefined;
  /**
   * Custom match predicate. Default matches `labelFn(item)` + `sublabelFn(item)`
   * via case-insensitive substring. Return `true` for all items if filtering
   * happens server-side (subscribe to `queryChanged`).
   */
  @Input() matchFn?: (item: T, query: string) => boolean;
  /** Optional custom suggestion row template — overrides the built-in label/sublabel layout. */
  @Input() itemTemplate?: TemplateRef<{ $implicit: T }>;

  @Output() itemSelected = new EventEmitter<T>();
  @Output() selectionChanged = new EventEmitter<T[]>();
  @Output() queryChanged = new EventEmitter<string>();

  private readonly host = inject(ElementRef<HTMLElement>);

  query = '';
  readonly open = signal(false);
  readonly selected = signal<T[]>([]);

  readonly results = computed<T[]>(() => {
    const q = this.query.trim().toLowerCase();
    const match = this.matchFn ?? this.defaultMatch.bind(this);
    const filtered = q ? this.items.filter((item) => match(item, q)) : this.items.slice();
    if (!this.multiple) return filtered;
    const pickedIds = new Set(this.selected().map((i) => this.trackByFn(i)));
    return filtered.filter((i) => !pickedIds.has(this.trackByFn(i)));
  });

  /**
   * `value` / `values` are honored on first set AND on subsequent
   * changes. Lets parents reset the picker via input rebinding.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('multiple' in changes || 'values' in changes || 'value' in changes) {
      if (this.multiple) {
        this.selected.set([...this.values]);
      } else {
        // Single mode: reflect `value` into the query field.
        this.query = this.value ? this.labelFn(this.value) : '';
      }
    }
  }

  onQueryChange(): void {
    this.open.set(true);
    this.queryChanged.emit(this.query);
  }

  select(item: T): void {
    if (this.multiple) {
      this.selected.update((s) => [...s, item]);
      this.query = '';
      this.itemSelected.emit(item);
      this.selectionChanged.emit(this.selected());
    } else {
      this.query = this.labelFn(item);
      this.open.set(false);
      this.itemSelected.emit(item);
    }
  }

  deselect(item: T): void {
    const id = this.trackByFn(item);
    this.selected.update((s) => s.filter((i) => this.trackByFn(i) !== id));
    this.selectionChanged.emit(this.selected());
  }

  /** Imperatively clear the multi-mode selection — handy after a parent's bulk op finishes. */
  clearSelection(): void {
    if (this.multiple) {
      this.selected.set([]);
      this.selectionChanged.emit([]);
    }
    this.query = '';
  }

  focusInput(): void {
    const input = this.host.nativeElement.querySelector('input');
    input?.focus();
  }

  effectiveEmptyText(): string {
    if (this.query) return this.noMatchesText;
    if (this.multiple && this.selected().length > 0 && this.items.length > 0) {
      return this.allPickedText;
    }
    return this.noItemsText;
  }

  /**
   * Outside-click closes the dropdown. `contains` keeps clicks inside
   * the picker (input + chip × + suggestion buttons) from auto-closing.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: Event): void {
    const target = event?.target as Node | null | undefined;
    if (target && this.host.nativeElement.contains(target)) return;
    this.open.set(false);
  }

  private defaultMatch(item: T, q: string): boolean {
    const label = this.labelFn(item).toLowerCase();
    if (label.includes(q)) return true;
    const sub = this.sublabelFn?.(item)?.toLowerCase();
    return sub ? sub.includes(q) : false;
  }
}
